import React, { useEffect, useState } from "react";
import { ChosenOptions } from "../BookingVehicleInfoCard/BookingVehicleInfoCard.props";
import { SelectVehicleExtrasProps } from "./SelectVehicleExtras.props";
import VehicleExtraOptions from "../../UI/VehicleExtraOptions";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import { t } from "i18next";
import BookingGetters from "../../../store/onlineBooking/getters";

// Default props
const defaultProps = {
  onChange: () => {},
};
// Constants
const SMALL_SCREEN_WIDTH = 768;
const ANDROID_SCREEN_WIDTH = 400;
const ALLOWED_ORIGINS = new Set([
  "https://mbrent.mercedes-benz.it",
  "https://bookingmerfina.mbrent.mercedes-benz.it",
  "https://mbrent.mercedes-benz.it/congaratulation",
  "https://merfinatestbooking.myrent.it",
  "https://mauroc147.myrent.it",
]);
// Helper functions
const getParentOrigin = () => {
  try {
    if (window.location.ancestorOrigins?.length > 0) {
      return window.location.ancestorOrigins[0];
    }
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch (error) {
    console.error("Error in getParentOrigin:", error);
    return "";
  }
};
const useScreenSize = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(
    window.innerWidth <= SMALL_SCREEN_WIDTH
  );
  const [isAndroidScreen, setIsAndroidScreen] = useState(
    window.innerWidth <= ANDROID_SCREEN_WIDTH
  );
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= SMALL_SCREEN_WIDTH);
      setIsAndroidScreen(window.innerWidth <= ANDROID_SCREEN_WIDTH);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return { isSmallScreen, isAndroidScreen };
};

const useParentResize = (
  totalHeightRecepExtrasFooter: number,
  isSmallScreen: boolean
) => {
  useEffect(() => {
    const parentOrigin = getParentOrigin();
    const targetOrigin =
      parentOrigin && ALLOWED_ORIGINS.has(parentOrigin) ? parentOrigin : "*";
    window.parent.postMessage(
      {
        event_id: "iframeHeight",
        data: {
          iframeHeight: isSmallScreen
            ? totalHeightRecepExtrasFooter
            : totalHeightRecepExtrasFooter,
        },
      },
      targetOrigin
    );
  }, [totalHeightRecepExtrasFooter, isSmallScreen]);
};

const ExtraOptionCard = ({
  option,
  selectedOptions,
  setSelectedOptions,
  cardBackgroundColor,
  selectedCardBackgroundColor,
  cardTextColor,
  selectedCardTextColor,
  secondaryColor,
  fillToCard,
  appSettings,
}) => {
  const handleDecrement = () => {
    setSelectedOptions((prev) => {
      const optionName = option.optionName;
      if (!prev[optionName]) return { ...prev };
      const updatedOptions = { ...prev };
      const optionDetails = { ...updatedOptions[optionName] };
      optionDetails.quantity = Math.max(optionDetails.quantity - 1, 0);
      if (optionDetails.quantity === 0) {
        delete updatedOptions[optionName];
      } else {
        updatedOptions[optionName] = optionDetails;
      }
      return updatedOptions;
    });
  };

  const handleIncrement = () => {
    setSelectedOptions((prev) => {
      const maxAllowed = option.maxAllowed || 1;
      const optionName = option.optionName;
      const updatedOptions = { ...prev };
      const optionDetails = updatedOptions[optionName]
        ? { ...updatedOptions[optionName] }
        : {
            quantity: 0,
            name: optionName,
            details: option.details,
            price: parseFloat(option.pricePerDayInUSD),
          };
      optionDetails.quantity = Math.min(optionDetails.quantity + 1, maxAllowed);
      updatedOptions[optionName] = optionDetails;
      return updatedOptions;
    });
  };
  return (
    <CardActionArea>
      <CardContent>
        <VehicleExtraOptions
          onDecrementClick={handleDecrement}
          onIncrementClick={handleIncrement}
          {...option}
          value={selectedOptions[option.optionName]?.quantity || 0}
          cardBackgroundColor={cardBackgroundColor}
          selectedCardBackgroundColor={selectedCardBackgroundColor}
          cardTextColor={cardTextColor}
          fillToCard={fillToCard}
          selectedCardTextColor={selectedCardTextColor}
          secondaryColor={secondaryColor}
          showOptionalCode={appSettings?.showOptionalCode}
          showOptionalDescription={appSettings?.showOptionalDescription}
          showDescFromBookingText={appSettings?.showDescFromBookingText}
          applyRateOptionalBooking={option.applyRateOptionalBooking}
        />
      </CardContent>
    </CardActionArea>
  );
};

const getGridStyle = (isSmallScreen: boolean): React.CSSProperties => ({
  marginLeft: !isSmallScreen ? "-36px" : "",
  overflowY: isSmallScreen ? ("auto" as const) : ("visible" as const),
  WebkitOverflowScrolling: "touch",
  // height: isSmallScreen ? "calc(100vh - 20%)" : "auto",
});

const getGridClassName = (isSmallScreen, isAndroidScreen) => {
  const base =
    "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 h-full box-border";
  const padding = !isSmallScreen ? "p-4" : "p-0 mt-[5%]";
  const marginBottom = isSmallScreen ? (isAndroidScreen ? "" : "") : "";
  return `${base} ${padding} ${marginBottom}`;
};

const getContainerStyle = (
  isSmallScreen: boolean,
  isAndroidScreen: boolean
): React.CSSProperties => ({
  marginTop: isSmallScreen && !isAndroidScreen ? "" : "",
  overflowY: "auto" as const,
  WebkitOverflowScrolling: "touch",
});

const SelectVehicleExtras = ({
  options,
  onChange = () => {},
  values = {},
  cardBackgroundColor = "",
  selectedCardBackgroundColor = "",
  cardTextColor = "",
  selectedCardTextColor = "",
  secondaryColor = "",
  fillToCard = false,
  insuranceList = {},
  totalHeightRecepExtrasFooter = 180,
}: SelectVehicleExtrasProps): JSX.Element => {
  const { isSmallScreen, isAndroidScreen } = useScreenSize();
  const [selectedOptions, setSelectedOptions] =
    useState<ChosenOptions["options"]>(values);
  const appSettings = BookingGetters.getAppSettings();

  useParentResize(totalHeightRecepExtrasFooter, isSmallScreen);

  useEffect(() => {
    onChange(selectedOptions);
  }, [selectedOptions]);

  const isOptionInInsurance = (optionName) =>
    insuranceList["Booking Insurance"]?.some(
      (e) => e.optionalsCode === optionName
    );
  const isDesktopScreen = window.innerWidth >= 1310;
  return (
    <div
      // className="h-full"
      // style={getContainerStyle(isSmallScreen, isAndroidScreen)}
      className="h-full scroll-container"
      style={{
        ...getContainerStyle(isSmallScreen, isAndroidScreen),
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-y",
        marginBottom: isSmallScreen ? "30%" : "",
        paddingLeft: isDesktopScreen ? "10px" : "",
      }}
    >
      <h2
        className="text-black mb-2"
        style={{
          gridColumn: "1 / -1",
          fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
          fontSize: !isSmallScreen ? "32px" : "24px",
          marginLeft: isSmallScreen ? "20px" : "",
        }}
      >
        {t("Add Extras")}
      </h2>
      <hr
        style={{
          border: "none",
          borderTop: "1px solid #000000",
          marginRight: isDesktopScreen ? "2%" : "undefined",
        }}
      />
      <div
        className={getGridClassName(isSmallScreen, isAndroidScreen)}
        style={getGridStyle(isSmallScreen)}
      >
        {options?.map(
          (option, index) =>
            !isOptionInInsurance(option.optionName) && (
              <ExtraOptionCard
                key={index}
                option={option}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                cardBackgroundColor={cardBackgroundColor}
                selectedCardBackgroundColor={selectedCardBackgroundColor}
                cardTextColor={cardTextColor}
                selectedCardTextColor={selectedCardTextColor}
                secondaryColor={secondaryColor}
                fillToCard={fillToCard}
                appSettings={appSettings}
              />
            )
        )}
      </div>
    </div>
  );
};

SelectVehicleExtras.defaultProps = defaultProps;
export { SelectVehicleExtras };
